import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { supabase } from "@/lib/supabase/client";
import {
  AuthContextType,
  Profile,
  UserRoles,
  Team,
  FamilyWeekend,
  UserRoleType,
} from "@/types";
import { User } from "@supabase/supabase-js";
import { logAuthAction } from "@/lib/activity-log";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Storage key for persisting switched dependent ID */
const SWITCHED_DEPENDENT_KEY = "staatsen_switched_dependent_id";

/**
 * Authentication Provider Component
 * Manages user authentication state and provides auth methods
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null);
  const [dependents, setDependents] = useState<Profile[]>([]);
  const [roles, setRoles] = useState<UserRoles>({
    isSuperAdmin: false,
    isFamilieAdmin: false,
    isUser: true,
    roles: ["user"],
  });
  const [activeWeekend, setActiveWeekend] = useState<FamilyWeekend | null>(null);
  const [userTeam, setUserTeam] = useState<Team | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSwitched, setIsSwitched] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  
  // Track if we should preserve the switched state during refresh
  const preserveSwitchedStateRef = useRef(false);

  /**
   * Fetch all user data after authentication
   * @param userId - The authenticated user's ID
   * @param preserveSwitchedState - If true, restore the switched dependent from storage
   */
  const fetchUserData = async (userId: string, preserveSwitchedState = false) => {
    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (profileError) {
        console.error("Error fetching profile:", profileError);
      } else if (profileData) {
        // Check if account is deleted
        if (profileData.deleted_at) {
          console.log("Account is deleted, signing out...");
          await supabase.auth.signOut();
          throw new Error("Dit account is verwijderd en niet meer toegankelijk.");
        }
        
        setProfile(profileData);
        // Check if user needs onboarding (only for non-dependent users)
        if (!profileData.is_dependent && !profileData.onboarding_completed_at) {
          setNeedsOnboarding(true);
        } else {
          setNeedsOnboarding(false);
        }
      }

      // Fetch dependents (users where this user is a guardian)
      const { data: dependentsData, error: dependentsError } = await supabase
        .from("profiles")
        .select("*")
        .eq("is_dependent", true)
        .contains("guardian_ids", [userId]);

      if (dependentsError) {
        console.error("Error fetching dependents:", dependentsError);
      } else {
        setDependents(dependentsData || []);
      }
      
      // Restore switched state from storage if available
      const savedDependentId = await AsyncStorage.getItem(SWITCHED_DEPENDENT_KEY);
      const dependentsList = dependentsData || [];
      
      if (savedDependentId && dependentsList.length > 0) {
        // Check if the saved dependent is still valid (in the dependents list)
        const savedDependent = dependentsList.find((d: Profile) => d.id === savedDependentId);
        if (savedDependent) {
          setActiveProfile(savedDependent);
          setIsSwitched(true);
          // Also fetch the dependent's team
          // We'll do this after we have the active weekend
        } else {
          // Saved dependent no longer valid, clear storage and use self
          await AsyncStorage.removeItem(SWITCHED_DEPENDENT_KEY);
          if (profileData) {
            setActiveProfile(profileData);
            setIsSwitched(false);
          }
        }
      } else if (profileData && !preserveSwitchedState) {
        // No saved dependent, set to self
        setActiveProfile(profileData);
        setIsSwitched(false);
      }

      // Fetch roles
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select("role, weekend_id")
        .eq("user_id", userId)
        .is("revoked_at", null);

      if (rolesError) {
        console.error("Error fetching roles:", rolesError);
      } else if (rolesData) {
        const rolesList = rolesData.map((r) => r.role as UserRoleType);
        setRoles({
          isSuperAdmin: rolesList.includes("super_admin"),
          isFamilieAdmin: rolesList.includes("familie_admin"),
          isUser: true,
          roles: rolesList.length > 0 ? rolesList : ["user"],
        });
      }

      // Fetch active weekend
      const { data: weekendData, error: weekendError } = await supabase
        .from("family_weekends")
        .select("*")
        .eq("is_active", true)
        .single();

      if (weekendError && weekendError.code !== "PGRST116") {
        console.error("Error fetching weekend:", weekendError);
        // No active weekend found - clear state
        setActiveWeekend(null);
        setUserTeam(null);
      } else if (weekendData) {
        setActiveWeekend(weekendData);

        // Determine which user's team to fetch (could be a dependent)
        const savedDependentId = await AsyncStorage.getItem(SWITCHED_DEPENDENT_KEY);
        const teamUserId = savedDependentId && dependentsData?.find((d: Profile) => d.id === savedDependentId) 
          ? savedDependentId 
          : userId;

        // Fetch team for the active profile (self or dependent)
        const { data: teamMemberData, error: teamError } = await supabase
          .from("team_members")
          .select(
            `
            team_id,
            teams (*)
          `
          )
          .eq("user_id", teamUserId);

        if (teamError) {
          console.error("Error fetching team:", teamError);
          setUserTeam(null);
        } else if (teamMemberData && teamMemberData.length > 0) {
          // Find team for active weekend
          const activeTeamMember = teamMemberData.find(
            (tm: any) => tm.teams?.weekend_id === weekendData.id
          );
          if (activeTeamMember?.teams) {
            setUserTeam(activeTeamMember.teams as unknown as Team);
          } else {
            setUserTeam(null);
          }
        } else {
          setUserTeam(null);
        }
      } else {
        // No active weekend - clear state
        setActiveWeekend(null);
        setUserTeam(null);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  /**
   * Clear all user state (including persisted switch)
   */
  const clearUserState = async () => {
    setUser(null);
    setProfile(null);
    setActiveProfile(null);
    setDependents([]);
    setIsSwitched(false);
    setRoles({
      isSuperAdmin: false,
      isFamilieAdmin: false,
      isUser: true,
      roles: ["user"],
    });
    setActiveWeekend(null);
    setUserTeam(null);
    setNeedsOnboarding(false);
    
    // Clear persisted switch
    try {
      await AsyncStorage.removeItem(SWITCHED_DEPENDENT_KEY);
    } catch (err) {
      console.error("Error clearing switched dependent:", err);
    }
  };

  /**
   * Switch to a dependent user
   * The guardian acts as the dependent for all app actions
   * Persists the switch to AsyncStorage so it survives app refreshes
   */
  const switchToDependent = async (dependentId: string) => {
    const dependent = dependents.find((d) => d.id === dependentId);
    if (dependent) {
      setActiveProfile(dependent);
      setIsSwitched(true);
      
      // Persist the switch to storage
      try {
        await AsyncStorage.setItem(SWITCHED_DEPENDENT_KEY, dependentId);
      } catch (err) {
        console.error("Error saving switched dependent:", err);
      }
      
      // Fetch the dependent's team for active weekend
      if (activeWeekend) {
        fetchDependentTeam(dependentId, activeWeekend.id);
      }
    }
  };

  /**
   * Switch back to the guardian's own profile
   * Clears the persisted switch from AsyncStorage
   */
  const switchBackToSelf = async () => {
    if (profile) {
      setActiveProfile(profile);
      setIsSwitched(false);
      
      // Clear the persisted switch
      try {
        await AsyncStorage.removeItem(SWITCHED_DEPENDENT_KEY);
      } catch (err) {
        console.error("Error clearing switched dependent:", err);
      }
      
      // Restore the guardian's team
      if (user && activeWeekend) {
        fetchUserTeam(user.id, activeWeekend.id);
      }
    }
  };

  /**
   * Fetch team for a specific user (dependent or self)
   */
  const fetchUserTeam = async (userId: string, weekendId: string) => {
    try {
      const { data: teamMemberData, error: teamError } = await supabase
        .from("team_members")
        .select(`
          team_id,
          teams (*)
        `)
        .eq("user_id", userId);

      if (teamError) {
        console.error("Error fetching team:", teamError);
        setUserTeam(null);
        return;
      }

      if (teamMemberData && teamMemberData.length > 0) {
        const activeTeamMember = teamMemberData.find(
          (tm: any) => tm.teams?.weekend_id === weekendId
        );
        if (activeTeamMember?.teams) {
          setUserTeam(activeTeamMember.teams as unknown as Team);
        } else {
          setUserTeam(null);
        }
      } else {
        setUserTeam(null);
      }
    } catch (error) {
      console.error("Error fetching user team:", error);
      setUserTeam(null);
    }
  };

  /**
   * Fetch team for a dependent user
   */
  const fetchDependentTeam = async (dependentId: string, weekendId: string) => {
    await fetchUserTeam(dependentId, weekendId);
  };

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          setUser(session.user);
          await fetchUserData(session.user.id);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        setUser(session.user);
        await fetchUserData(session.user.id);
      } else if (event === "SIGNED_OUT") {
        clearUserState();
      } else if (event === "TOKEN_REFRESHED" && session?.user) {
        setUser(session.user);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Sign in with email and password
   */
  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;

      // Log successful login
      if (data.user) {
        logAuthAction(data.user.id, "login", { email });
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Sign up with email, password, full name, and family password
   * Note: Profile and default 'user' role are created automatically by database trigger
   */
  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    familyPassword: string
  ) => {
    setIsLoading(true);
    try {
      // Verify family password
      const { data: isValid, error: verifyError } = await supabase.rpc(
        "verify_signup_password",
        { p_password: familyPassword }
      );

      if (verifyError) {
        throw new Error("Fout bij het verifiëren van het familie wachtwoord");
      }

      if (!isValid) {
        throw new Error("Ongeldig familie wachtwoord");
      }

      // Create account with full_name in metadata
      // Database trigger (handle_new_user) automatically creates profile and assigns 'user' role
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: "staatsen://auth/callback",
        },
      });

      if (signUpError) throw signUpError;
      if (!authData.user) throw new Error("Account aanmaken mislukt");

      // Log successful signup
      logAuthAction(authData.user.id, "signup", { email, full_name: fullName });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Sign out the current user
   */
  const signOut = async () => {
    setIsLoading(true);
    try {
      // Log logout before signing out (while we still have the user)
      if (user) {
        logAuthAction(user.id, "logout");
      }

      // Clear switched state from storage
      await AsyncStorage.removeItem(SWITCHED_DEPENDENT_KEY);

      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh user profile and related data
   */
  const refreshProfile = async () => {
    if (user) {
      await fetchUserData(user.id);
    }
  };

  /**
   * Check if profile is incomplete (missing key fields)
   */
  const isProfileIncomplete = (profileData: Profile | null): boolean => {
    if (!profileData) return false;
    // Check if any of these important fields are missing
    return !profileData.nickname || !profileData.date_of_birth;
  };

  /**
   * Mark user onboarding as complete
   * Updates the database and local state
   */
  const completeOnboarding = async () => {
    if (!user?.id) return;

    try {
      const timestamp = new Date().toISOString();
      
      const { error } = await supabase
        .from("profiles")
        .update({ onboarding_completed_at: timestamp })
        .eq("id", user.id);

      if (error) throw error;

      // Update local profile state
      if (profile) {
        const updatedProfile = { ...profile, onboarding_completed_at: timestamp };
        setProfile(updatedProfile);
        
        // Check if profile needs completion after onboarding
        if (isProfileIncomplete(updatedProfile)) {
          setNeedsProfileCompletion(true);
        }
      }
      
      setNeedsOnboarding(false);
    } catch (error) {
      console.error("Error completing onboarding:", error);
      throw error;
    }
  };

  /**
   * Mark profile completion as done (either saved or skipped)
   */
  const completeProfileSetup = async () => {
    setNeedsProfileCompletion(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        activeProfile,
        dependents,
        roles,
        activeWeekend,
        userTeam,
        isLoading,
        isInitialized,
        isSwitched,
        needsOnboarding,
        needsProfileCompletion,
        signIn,
        signUp,
        signOut,
        refreshProfile,
        switchToDependent,
        switchBackToSelf,
        completeOnboarding,
        completeProfileSetup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

